import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from '../../utils/theme';
import { Target, TrendingUp, TrendingDown, Info } from 'lucide-react';
import DemoModeBanner from '../DemoModeBanner';

/**
 * ShotValueMap - Interactive basketball court with shot zone visualization
 *
 * Features:
 * - Color-coded shot zones (green = high PPP, red = avoid)
 * - Player-specific efficiency overlay
 * - Click zones for detailed breakdown
 * - Defender-adjusted recommendations
 */

// Shot zone definitions matching backend
const SHOT_ZONES = [
  {
    id: 'restricted',
    name: 'Restricted Area',
    path: 'M 250 470 A 40 40 0 1 1 250 390 A 40 40 0 1 1 250 470 Z',
    leagueAvg: 61,
    description: 'Shots within 4 feet of basket'
  },
  {
    id: 'paint',
    name: 'In The Paint (Non-RA)',
    path: 'M 170 470 L 170 375 L 330 375 L 330 470 L 290 470 A 40 40 0 1 0 210 470 Z',
    leagueAvg: 40,
    description: 'Paint shots outside restricted area'
  },
  {
    id: 'midrange-left',
    name: 'Mid-Range Left',
    path: 'M 50 470 L 50 280 Q 125 200 250 180 L 170 375 L 170 470 Z',
    leagueAvg: 37,
    description: 'Left side mid-range jumpers'
  },
  {
    id: 'midrange-right',
    name: 'Mid-Range Right',
    path: 'M 450 470 L 450 280 Q 375 200 250 180 L 330 375 L 330 470 Z',
    leagueAvg: 37,
    description: 'Right side mid-range jumpers'
  },
  {
    id: 'midrange-top',
    name: 'Mid-Range Top',
    path: 'M 170 375 L 250 180 L 330 375 Z',
    leagueAvg: 37,
    description: 'Top of key mid-range'
  },
  {
    id: 'corner3-left',
    name: 'Corner 3 Left',
    path: 'M 0 470 L 0 280 L 50 280 L 50 470 Z',
    leagueAvg: 39,
    description: 'Left corner three-pointer'
  },
  {
    id: 'corner3-right',
    name: 'Corner 3 Right',
    path: 'M 500 470 L 500 280 L 450 280 L 450 470 Z',
    leagueAvg: 39,
    description: 'Right corner three-pointer'
  },
  {
    id: 'above3-left',
    name: 'Above Break 3 Left',
    path: 'M 0 280 L 0 50 Q 125 0 250 0 L 250 180 Q 125 200 50 280 Z',
    leagueAvg: 35,
    description: 'Left wing three-pointer'
  },
  {
    id: 'above3-right',
    name: 'Above Break 3 Right',
    path: 'M 500 280 L 500 50 Q 375 0 250 0 L 250 180 Q 375 200 450 280 Z',
    leagueAvg: 35,
    description: 'Right wing three-pointer'
  }
];

// Sample player data (would come from API)
const SAMPLE_PLAYER_ZONES = {
  'restricted': { percentage: 62.2, ppp: 1.24, attempts: 45, makes: 28 },
  'paint': { percentage: 40.9, ppp: 0.82, attempts: 22, makes: 9 },
  'midrange-left': { percentage: 35.5, ppp: 0.71, attempts: 15, makes: 5 },
  'midrange-right': { percentage: 38.1, ppp: 0.76, attempts: 16, makes: 6 },
  'midrange-top': { percentage: 33.3, ppp: 0.67, attempts: 12, makes: 4 },
  'corner3-left': { percentage: 42.9, ppp: 1.29, attempts: 14, makes: 6 },
  'corner3-right': { percentage: 40.0, ppp: 1.20, attempts: 15, makes: 6 },
  'above3-left': { percentage: 34.6, ppp: 1.04, attempts: 26, makes: 9 },
  'above3-right': { percentage: 36.4, ppp: 1.09, attempts: 22, makes: 8 }
};

/**
 * Get color based on PPP value
 */
function getZoneColor(ppp, isDarkMode) {
  if (ppp >= 1.15) return 'rgba(34, 197, 94, 0.7)'; // Green
  if (ppp >= 1.0) return 'rgba(59, 130, 246, 0.7)'; // Blue
  if (ppp >= 0.85) return 'rgba(234, 179, 8, 0.7)'; // Yellow
  return 'rgba(239, 68, 68, 0.7)'; // Red
}

/**
 * Get recommendation text
 */
function getRecommendation(ppp, leagueAvg, percentage) {
  const vsLeague = percentage - leagueAvg;
  if (ppp >= 1.15 && vsLeague >= 0) return { text: 'HIGH VALUE - seek these shots', type: 'success' };
  if (ppp >= 1.0) return { text: 'GOOD VALUE - take when open', type: 'info' };
  if (ppp >= 0.85) return { text: 'AVERAGE - acceptable situationally', type: 'warning' };
  return { text: 'AVOID - look for better options', type: 'error' };
}

export default function ShotValueMap({ playerId, playerName = 'Player' }) {
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const [selectedZone, setSelectedZone] = useState(null);
  const [hoveredZone, setHoveredZone] = useState(null);
  const [playerZones, setPlayerZones] = useState(SAMPLE_PLAYER_ZONES);
  const [loading, setLoading] = useState(false);

  // Calculate summary stats
  const summary = useMemo(() => {
    const zones = Object.entries(playerZones);
    const totalAttempts = zones.reduce((sum, [, z]) => sum + z.attempts, 0);
    const totalMakes = zones.reduce((sum, [, z]) => sum + z.makes, 0);
    const avgPPP = zones.reduce((sum, [, z]) => sum + z.ppp * z.attempts, 0) / totalAttempts;

    // Find best and worst zones
    const sorted = zones.sort((a, b) => b[1].ppp - a[1].ppp);
    const bestZone = SHOT_ZONES.find(z => z.id === sorted[0][0]);
    const worstZone = SHOT_ZONES.find(z => z.id === sorted[sorted.length - 1][0]);

    return {
      totalAttempts,
      totalMakes,
      overallPercentage: (totalMakes / totalAttempts * 100).toFixed(1),
      overallPPP: avgPPP.toFixed(2),
      bestZone: bestZone?.name,
      worstZone: worstZone?.name
    };
  }, [playerZones]);

  // Fetch player zones from API
  useEffect(() => {
    if (playerId) {
      setLoading(true);
      // In production, fetch from: /api/basketball/shot-zones/:playerId
      // For now, use sample data with slight delay to simulate API
      setTimeout(() => {
        setPlayerZones(SAMPLE_PLAYER_ZONES);
        setLoading(false);
      }, 300);
    }
  }, [playerId]);

  const activeZone = selectedZone || hoveredZone;
  const activeZoneData = activeZone ? {
    ...SHOT_ZONES.find(z => z.id === activeZone),
    ...playerZones[activeZone]
  } : null;

  return (
    <div className="space-y-4">
      <DemoModeBanner compact message="Shot zones use sample data. Real shot charts load when player data is available." />
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className={`text-lg font-semibold ${'text-white'}`}>
            Shot Value Map
          </h3>
          <p className={`text-sm ${'text-gray-400'}`}>
            {playerName} - Click zones for details
          </p>
        </div>
        <Target className="text-orange-500" size={24} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Court SVG */}
        <div className={`lg:col-span-2 p-4 rounded-xl ${'bg-gray-900'}`}>
          <svg
            viewBox="0 0 500 500"
            className="w-full h-auto"
            style={{ maxHeight: '400px' }}
          >
            {/* Court background */}
            <rect x="0" y="0" width="500" height="500" fill={'#1a1a2e'} />

            {/* Court lines */}
            <rect x="0" y="0" width="500" height="470" fill="none" stroke={'#333'} strokeWidth="2" />

            {/* Three-point arc */}
            <path
              d="M 50 470 L 50 280 Q 50 50 250 50 Q 450 50 450 280 L 450 470"
              fill="none"
              stroke={'#444'}
              strokeWidth="2"
            />

            {/* Paint/Key */}
            <rect x="170" y="375" width="160" height="95" fill="none" stroke={'#444'} strokeWidth="2" />

            {/* Restricted area */}
            <circle cx="250" cy="430" r="40" fill="none" stroke={'#444'} strokeWidth="2" />

            {/* Basket */}
            <circle cx="250" cy="450" r="8" fill={'#f97316'} />
            <rect x="235" y="455" width="30" height="3" fill={'#f97316'} />

            {/* Free throw line */}
            <line x1="170" y1="375" x2="330" y2="375" stroke={'#444'} strokeWidth="2" />

            {/* Shot zones (clickable) */}
            {SHOT_ZONES.map(zone => {
              const zoneData = playerZones[zone.id];
              const isActive = activeZone === zone.id;
              const color = zoneData ? getZoneColor(zoneData.ppp, isDarkMode) : 'rgba(128, 128, 128, 0.3)';

              return (
                <g key={zone.id}>
                  <path
                    d={zone.path}
                    fill={color}
                    stroke={isActive ? '#f97316' : '#555'}
                    strokeWidth={isActive ? 3 : 1}
                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                    onMouseEnter={() => setHoveredZone(zone.id)}
                    onMouseLeave={() => setHoveredZone(null)}
                    onClick={() => setSelectedZone(selectedZone === zone.id ? null : zone.id)}
                  />
                  {/* Zone PPP label */}
                  {zoneData && (
                    <text
                      x={zone.id.includes('left') ? 75 : zone.id.includes('right') ? 425 : 250}
                      y={zone.id.includes('corner') ? 380 : zone.id.includes('above') ? 150 : zone.id === 'restricted' ? 440 : zone.id === 'paint' ? 410 : 300}
                      textAnchor="middle"
                      fill={'#fff'}
                      fontSize="14"
                      fontWeight="bold"
                      style={{ pointerEvents: 'none' }}
                    >
                      {zoneData.ppp.toFixed(2)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Legend */}
          <div className="flex justify-center gap-4 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(34, 197, 94, 0.7)' }} />
              <span className={`text-xs ${'text-gray-400'}`}>High (1.15+ PPP)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(59, 130, 246, 0.7)' }} />
              <span className={`text-xs ${'text-gray-400'}`}>Good (1.0+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(234, 179, 8, 0.7)' }} />
              <span className={`text-xs ${'text-gray-400'}`}>Avg (0.85+)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: 'rgba(239, 68, 68, 0.7)' }} />
              <span className={`text-xs ${'text-gray-400'}`}>Avoid (&lt;0.85)</span>
            </div>
          </div>
        </div>

        {/* Zone Details Panel */}
        <div className={`p-4 rounded-xl ${'bg-gray-900 border border-gray-800'}`}>
          {activeZoneData ? (
            <div className="space-y-4">
              <h4 className={`font-semibold ${'text-white'}`}>
                {activeZoneData.name}
              </h4>
              <p className={`text-sm ${'text-gray-400'}`}>
                {activeZoneData.description}
              </p>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className={'text-gray-400'}>PPP</span>
                  <span className={`font-bold ${'text-white'}`}>
                    {activeZoneData.ppp?.toFixed(2) || '--'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={'text-gray-400'}>FG%</span>
                  <span className={`font-bold ${'text-white'}`}>
                    {activeZoneData.percentage?.toFixed(1) || '--'}%
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={'text-gray-400'}>Attempts</span>
                  <span className={'text-white'}>
                    {activeZoneData.makes || 0}/{activeZoneData.attempts || 0}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className={'text-gray-400'}>vs League</span>
                  <span className={`flex items-center gap-1 ${
                    (activeZoneData.percentage || 0) >= activeZoneData.leagueAvg ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {(activeZoneData.percentage || 0) >= activeZoneData.leagueAvg ?
                      <TrendingUp size={14} /> : <TrendingDown size={14} />}
                    {((activeZoneData.percentage || 0) - activeZoneData.leagueAvg).toFixed(1)}%
                  </span>
                </div>
              </div>

              {/* Recommendation */}
              {activeZoneData.ppp && (
                <div className={`p-3 rounded-lg ${
                  getRecommendation(activeZoneData.ppp, activeZoneData.leagueAvg, activeZoneData.percentage).type === 'success'
                    ? 'bg-green-500/10 border border-green-500/30'
                    : getRecommendation(activeZoneData.ppp, activeZoneData.leagueAvg, activeZoneData.percentage).type === 'info'
                      ? 'bg-blue-500/10 border border-blue-500/30'
                      : getRecommendation(activeZoneData.ppp, activeZoneData.leagueAvg, activeZoneData.percentage).type === 'warning'
                        ? 'bg-yellow-500/10 border border-yellow-500/30'
                        : 'bg-red-500/10 border border-red-500/30'
                }`}>
                  <p className={`text-sm font-medium ${'text-white'}`}>
                    {getRecommendation(activeZoneData.ppp, activeZoneData.leagueAvg, activeZoneData.percentage).text}
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <Info size={32} className={'text-gray-600'} />
              <p className={`mt-2 ${'text-gray-400'}`}>
                Click or hover over a zone to see details
              </p>
            </div>
          )}

          {/* Summary Stats */}
          <div className={`mt-4 pt-4 border-t ${'border-gray-800'}`}>
            <h5 className={`text-sm font-medium mb-2 ${'text-gray-300'}`}>
              Summary
            </h5>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className={'text-gray-400'}>Overall PPP</span>
                <span className={'text-white'}>{summary.overallPPP}</span>
              </div>
              <div className="flex justify-between">
                <span className={'text-gray-400'}>Best Zone</span>
                <span className="text-green-500 text-xs">{summary.bestZone}</span>
              </div>
              <div className="flex justify-between">
                <span className={'text-gray-400'}>Avoid</span>
                <span className="text-red-500 text-xs">{summary.worstZone}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
